import { DeleteOutlined, EditOutlined, ExclamationCircleOutlined, FilterOutlined, PlusOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Input, Modal, Select, Table, Tabs, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { RootState } from '../../app/store';
import SimpleButton from '../../components/buttons/simpleButton/SimpleButton';
import { deleteTraining, fetchTrainingData, initiateTraining, resetDeleteTraining, resetFetchTrainingData, resetInitiateTraining, resetUpdateTraining, updateTraining } from '../../features/training/trainingSlice';
import CreateTrainingData from './components/trainingData/createTrainingData';
import './training.scss';

const { confirm } = Modal;

export type TrainingDataItem = {
    id: number;
    type: string;
    question: string;
    sql: string[];
    tags?: string[];
};


const TrainingQuestion: React.FC<{ question: string }> = ({ question }) => {
    return <div className="training-question">{question}</div>;
};

const TrainingContent: React.FC<{ content: string }> = ({ content }) => {
    return <div className="training-content">{content}</div>;
};



const Training: React.FC = () => {

    const dispatch = useDispatch<any>();
    
    const { trainingData = [], isLoading } = useSelector((state: RootState) => state.training.fetchTrainingData)
    const { isLoading: isLoadingInitiateTraining, isSuccess: isSuccessInitiateTraining, isError: isErrorInitiateTraining } = useSelector((state: RootState) => state.training.initiateTraining)
    const { isLoading: isLoadingUpdateTraining, isSuccess: isSuccessUpdateTraining } = useSelector((state: RootState) => state.training.updateTraining)

    const { isSuccess: isSuccessDeleteTraining, isError: isErrorDeleteTraining } = useSelector((state: RootState) => state.training.deleteTraining)

    const [filterType, setFilterType] = useState<string>('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingData, setEditingData] = useState<TrainingDataItem | undefined>(undefined);
    const [pageNumber, setpageNumber] = useState(1)
    const [pageSize, setPageSize] = useState<number>(10)
    const [selectedTrainingIdforDeletion, setSelectedTrainingIdforDeletion] = useState<number|null>(null)
    const [searchquery, setSearchquery] = useState<string>('')


    const filterOptions = [
        { label: 'None', value: '' },
        { label: 'SQL', value: 'question_sql' },
        { label: 'Documentation', value: 'doc' },
        { label: 'DDL', value: 'ddl' }
    ];

    useEffect(() => {
        dispatch(fetchTrainingData({ searchQuery: searchquery, filterType: filterType, pageNumber: pageNumber, pageSize: pageSize }))

        return () => {
            dispatch(resetFetchTrainingData())
        }
    }, [])

    useEffect(() => {
        if (isSuccessInitiateTraining) {
            toast.success("Successfully Completed Training");
            setIsModalVisible(false);
            dispatch(fetchTrainingData({ searchQuery: searchquery, filterType: filterType, pageNumber: pageNumber, pageSize: pageSize }))
        } else if (isErrorInitiateTraining) {
            toast.error("Failed to initiate training.Please try again");
        }

        return () => {
            dispatch(resetInitiateTraining())
        }

    }, [isSuccessInitiateTraining, isErrorInitiateTraining])


    useEffect(() => {
        if (isSuccessDeleteTraining) {
            toast.success("Successfully deleted training data");
            dispatch(fetchTrainingData({ searchQuery: searchquery, filterType: filterType, pageNumber: pageNumber, pageSize: pageSize }))
        } else if (isErrorDeleteTraining) {
            toast.error("Failed to delete training data");
        }

        return () => {
            dispatch(resetDeleteTraining())
        }
    }, [isSuccessDeleteTraining, isErrorDeleteTraining])


    useEffect(() => {
        if (isSuccessUpdateTraining) {
            dispatch(fetchTrainingData({ searchQuery: searchquery, filterType: filterType, pageNumber: pageNumber, pageSize: pageSize }))
            handleModalCancel()
        }
        return () => {
            dispatch(resetUpdateTraining())
        }
    }, [isSuccessUpdateTraining])



    const showAddModal = () => {
        setEditingData(undefined);
        setIsModalVisible(true);
    };

    const showEditModal = (record: TrainingDataItem) => {
        console.log('record', record);
        setEditingData({
            ...record,
            type: record.type
        });
        setIsModalVisible(true);
    };

    const handleModalCancel = () => {
        setIsModalVisible(false);
        setEditingData(undefined);
    };

    const handleSaveTrainingData = (data: any, isEditing = false) => {
        console.log('Saving training data:', data);
        if (isEditing) {
            dispatch(updateTraining(data))
        } else {

            dispatch(initiateTraining(data))
        }

        setEditingData(undefined);
    };


    const handleConfirmTrainingData = () => {
        const payload = {
            "training_id":selectedTrainingIdforDeletion?.toString()
        }

        dispatch(deleteTraining(payload))

    }

    const handleDeleteTrainingData = (id: number, selectedQuestion: string) => {
        setSelectedTrainingIdforDeletion(id)        

        // Display confirmation modal
        confirm({
            title: "Delete Training Data",
            icon: <ExclamationCircleOutlined />,
            centered: true,
            content: (<p>Are you sure you want to delete  <b>{selectedQuestion}</b> Training data ?</p>),
            onOk() {
                handleConfirmTrainingData()
            },
            onCancel() { },
        });
    }

    const handlePageChange = (page: number, pageSize: number) => {
        setpageNumber(page)
        setPageSize(pageSize);
        dispatch(fetchTrainingData({ searchQuery: searchquery, filterType: filterType, pageNumber: page, pageSize: pageSize }))
    }
    const columns: ColumnsType<any> = [
        {
            title: 'QUESTION',
            dataIndex: 'question',
            key: 'question',
            render: (text: string) => <TrainingQuestion question={text} />,
        },
        {
            title: 'CONTENT',
            dataIndex: 'sql',
            key: 'sql',
            render: (text: string) => <TrainingContent content={text} />,
        },
        {
            title: 'TRAINING DATA TYPE',
            dataIndex: 'type',
            key: 'type',
        },
        {
            title: 'ACTION',
            key: 'action',
            render: (_: any, record: any) => (
                <div className="action-buttons">
                    <Button  icon={<EditOutlined />} className="edit-button" onClick={() => showEditModal(record)} />
                    <Button  icon={<DeleteOutlined />} className="delete-button"  onClick={() => handleDeleteTrainingData(record?.id,record?.question)} />
                </div>
            ),
        },
    ];

    const handleSearch = () => {
        dispatch(fetchTrainingData({ searchQuery: searchquery, filterType: filterType, pageNumber: pageNumber, pageSize: pageSize }))
    }

    const handleFilterTypeSelect = (value: string) => {
        setFilterType(value)
        setpageNumber(1)
        dispatch(fetchTrainingData({ searchQuery: searchquery, filterType: value, pageNumber: 1, pageSize: pageSize }))
    }
    return (
        <div className="training-container">
            <div className="training-header">
                <div className="header-title">
                    <h2 >Training Data</h2>
                </div>
                <div className="header-buttons">
                    <Button type="primary" icon={<PlusOutlined />} className="add-training-data-button" onClick={showAddModal}          >
                        Add training data
                    </Button>
                </div>
            </div>

            <div className="training-content-section">
                <div className="training-search-bar">
                    <Input placeholder="Search..." prefix={<SearchOutlined />} className="search-input" value={searchquery} onChange={(e) => setSearchquery(e.target.value)} />
                    <SimpleButton
                        text={""}
                        color="primary"
                        icon={<SearchOutlined />}
                        onClick={handleSearch}
                    />

                    <Select
                        placeholder="Filter by type"
                        style={{ width: 150, marginLeft: 16 }}
                        options={filterOptions}
                        value={filterType || undefined}
                        onChange={(value) => handleFilterTypeSelect(value)}
                        suffixIcon={<FilterOutlined />}
                    />

                    <div className="right-actions">
                        <Button icon={<ReloadOutlined />}
                            className="reload-button"
                            onClick={() => dispatch(fetchTrainingData({ searchQuery: searchquery, filterType: filterType, pageNumber: pageNumber, pageSize: pageSize }))}
                        />
                    </div>
                </div>

                <Table
                    loading={isLoading}
                    columns={columns}
                    dataSource={trainingData?.training_data}
                    className="training-table"
                    scroll={{ x: "auto" }}
                    rowClassName={(_record, index) => index % 2 === 0 ? "table-row-light" : "table-row-dark"}                  
                    pagination={{
                        position: ["bottomLeft"],
                        pageSize: pageSize,
                        current: pageNumber,
                        onChange: handlePageChange,
                        total: trainingData?.training_data_count,
                        showSizeChanger: true,                      
                        pageSizeOptions: [5, 10, 20, 50, 100, 200, 500],
                        showTotal: (total, range) => (
                            <div className="pagniation-text">
                                {`${range[0]}-${range[1]} of ${total} items`}
                            </div>
                        ),
                    }}
                />
            </div>

            <CreateTrainingData
                visible={isModalVisible}
                onCancel={handleModalCancel}
                onSave={handleSaveTrainingData}
                editData={editingData}

            />
        </div>
    );
};

export default Training;